<script setup lang="ts">
    const statsBackgroundSvg = '/img/2026/stats-background.svg';
    import CanvasMorphShape from '~/components/fragments/canvas-morph-shape.vue';

    interface CarouselCard {
        largeText: string;
        smallText: string;
        shapeIndex?: number;
    }

    interface StatsButton {
        label?: string;
        title?: string;
        url?: string;
    }

    const props = withDefaults(defineProps<{
        headline?: string;
        copy?: string;
        button?: StatsButton;
        carouselCards?: CarouselCard[];
    }>(), {
        headline: '',
        copy: '',
    });

    const defaultStats = [
        { value: '13,000', label: 'In-person attendees' },
        { value: '300+', label: 'Breakout sessions' },
        { value: '200+', label: 'Expert speakers' },
        { value: '3', label: 'Days of inspiration' },
    ];

    const stats = computed(() => {
        if (props.carouselCards?.length) {
            return props.carouselCards.map(card => ({
                value: card.largeText,
                label: card.smallText,
            }));
        }
        return defaultStats;
    });

    const parsedStats = computed(() => stats.value.map((s) => {
        const match = s.value.match(/^([\d,]+)(.*)$/);
        return {
            label: s.label,
            numeric: match ? parseInt(match[1].replace(/,/g, ''), 10) : 0,
            suffix: match ? match[2] : '',
        };
    }));

    const SLIDE_DURATION = 10; // seconds
    const CIRCUMFERENCE = 2 * Math.PI * 20; // ~125.66, for r=20 SVG circle

    // Shape paths — one per stat slide (same command structure for morphing)
    // Offsets are in the box's coordinate space — they scale naturally with the box
    const shapePathDefs = [
        { d: 'M78.8731 247.686L40.2535 453.02C36.2252 474.437 50.0832 495.138 71.4197 499.576L507.667 590.301C529.556 594.854 550.926 580.58 555.106 558.617L593.971 354.384C596.755 339.755 591.182 324.791 579.508 315.547L381.356 158.641C371.387 150.747 358.257 148.063 345.989 151.413L107.648 216.492C92.8762 220.525 81.7035 232.637 78.8731 247.686Z', x: 40, y: -10, mx: 30, my: 0 },
        { d: 'M153.444 363.431L210.874 492.717C216.328 504.994 227.581 513.714 240.831 515.931L622.048 579.692C644.046 583.371 664.805 568.348 668.185 546.301L704.523 309.265C707.578 289.334 695.32 270.244 675.924 264.729L277.8 151.53C259.176 146.235 239.432 155.04 230.927 172.434L154.066 329.621C148.871 340.245 148.643 352.623 153.444 363.431Z', x: -50, y: 0, mx: -40, my: 5 },
        { d: 'M171.603 406.527L381.018 515.009C392.784 521.104 406.806 520.976 418.46 514.667L537.055 450.461C547.268 444.931 554.574 435.235 557.074 423.892L606.712 198.61C612.435 172.636 591.687 148.414 565.142 150.081L211.693 172.276C192.911 173.455 177.491 187.568 174.656 206.172L150.458 364.984C147.876 381.932 156.381 398.642 171.603 406.527Z', x: -5, y: 50, mx: -5, my: 30 },
        { d: 'M150.924 244.539L188.024 415.056C191.661 431.774 205.529 444.328 222.525 446.289L452.947 472.876C469.323 474.765 485.181 466.415 492.89 451.843L544.155 354.931C549.815 344.231 550.325 331.544 545.543 320.424L482.66 174.2C475.309 157.108 457.144 147.341 438.832 150.635L182.928 196.667C160.626 200.678 146.107 222.397 150.924 244.539Z', x: 20, y: 50, mx: 10, my: 30 },
    ];
    const shapePaths = computed(() =>
        shapePathDefs.map(p => ({
            d: p.d,
            x: isMobile.value ? p.mx : p.x,
            y: isMobile.value ? p.my : p.y,
        }))
    );

    const SHAPE_INNER_SHADOWS = [
        { x: 0, y: 0, blur: 20, spread: 0, color: '#FF8502' },
        { x: 0, y: 0, blur: 95, spread: 0, color: '#FF9700' },
        { x: 0, y: 0, blur: 0, spread: 0, color: '#FF9700' },
    ];

    const SHAPE_DROP_SHADOWS = [{ x: 0, y: 0, blur: 150, spread: 0, color: '#D63D00' }];

    // -----------------------------------------------------------------------
    // Box model: fixed internal size, visually scales to fit available space
    // -----------------------------------------------------------------------
    const BOX_W_DESKTOP = 566;
    const BOX_H_DESKTOP = 452;
    const BOX_W_MOBILE = 430;
    const BOX_H_MOBILE = 290;
    const BLEED_DESKTOP = 200;
    const BLEED_MOBILE = 50;
    const isMobile = ref(false);
    const bleed = computed(() => isMobile.value ? BLEED_MOBILE : BLEED_DESKTOP);
    const BOX_W = computed(() => isMobile.value ? BOX_W_MOBILE : BOX_W_DESKTOP);
    const BOX_H = computed(() => isMobile.value ? BOX_H_MOBILE : BOX_H_DESKTOP);

    // Visual size bounds per viewport range (max/min in CSS px)
    // - max: box won't be visually wider than this (centers if there's more room)
    // - min: box won't be visually narrower than this (clips if container is smaller)
    function computeBoxScale(availableWidth) {
        const vw = window.innerWidth;
        const boxW = BOX_W.value;
        const maxV = vw < 768 ? Infinity : boxW;
        const visual = Math.max(0, Math.min(availableWidth, maxV));
        return Math.min(1, visual / boxW);
    }

    const carouselWrapRef = ref(null);
    const boxScale = ref(1);
    let resizeObs = null;

    // Wrapper: collapses layout space to match the box's visual size
    const boxWrapperStyle = computed(() => {
        const s = boxScale.value;
        if (s >= 1) return {};
        return {
            width: `${Math.round(BOX_W.value * s)}px`,
            height: `${Math.round(BOX_H.value * s)}px`,
        };
    });

    // Box: fixed internal dimensions, scales via transform
    const boxStyle = computed(() => {
        const s = boxScale.value;
        const style = { width: `${BOX_W.value}px`, height: `${BOX_H.value}px` };
        if (s < 1) {
            style.transform = `scale(${s})`;
            style.transformOrigin = 'top left';
        }
        return style;
    });

    const currentIndex = ref(0);
    const shapeCounter = ref(0);
    const currentShapeIndex = computed(() => {
        const card = props.carouselCards?.[currentIndex.value];
        if (card?.shapeIndex != null) {
            return card.shapeIndex % shapePaths.value.length;
        }
        return shapeCounter.value % shapePaths.value.length;
    });

    watch(currentIndex, () => {
        const card = props.carouselCards?.[currentIndex.value];
        if (card?.shapeIndex == null) {
            shapeCounter.value++;
        }
    });
    const morphShapeEl = ref(null);

    // Count-up animation
    const countUpValue = ref(0);
    const countUpDone = ref(true);
    let countUpRaf = null;
    const reducedMotion = ref(false);

    function startCountUp() {
        if (countUpRaf) cancelAnimationFrame(countUpRaf);

        const target = parsedStats.value[currentIndex.value].numeric;

        if (reducedMotion.value) {
            countUpValue.value = target;
            countUpDone.value = true;
            return;
        }

        countUpValue.value = 0;
        countUpDone.value = false;
        const duration = 1500;
        const startTime = performance.now();

        const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            countUpValue.value = Math.floor(target * eased);
            if (progress < 1) {
                countUpRaf = requestAnimationFrame(animate);
            } else {
                countUpValue.value = target;
                countUpDone.value = true;
            }
        };
        countUpRaf = requestAnimationFrame(animate);
    }

    const formattedValue = computed(() => {
        const stat = parsedStats.value[currentIndex.value];
        return countUpValue.value.toLocaleString('en-US') + (countUpDone.value ? stat.suffix : '');
    });

    const isPaused = ref(false);
    const userPaused = ref(false);
    const timerKey = ref(0);
    let autoplayTimer = null;
    let resumeTimer = null;
    const sectionRef = ref(null);

    const startAutoplay = () => {
        stopAutoplay();
        autoplayTimer = setInterval(() => {
            currentIndex.value = (currentIndex.value + 1) % stats.value.length;
            timerKey.value++;
        }, SLIDE_DURATION * 1000);
    };

    const stopAutoplay = () => {
        if (autoplayTimer) {
            clearInterval(autoplayTimer);
            autoplayTimer = null;
        }
    };

    const togglePause = () => {
        userPaused.value = !userPaused.value;
        isPaused.value = userPaused.value;
        if (isPaused.value) {
            stopAutoplay();
        } else {
            timerKey.value++;
            startAutoplay();
        }
    };

    const goToSlide = (index) => {
        currentIndex.value = index;
        timerKey.value++;
        if (!isPaused.value) {
            startAutoplay();
        }
    };

    onMounted(() => {
        reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        isMobile.value = window.innerWidth < 768;
        if (reducedMotion.value) {
            isPaused.value = true;
        } else {
            startAutoplay();
        }
        startCountUp();

        // Measure available width → compute box scale
        if (carouselWrapRef.value) {
            resizeObs = new ResizeObserver(([entry]) => {
                const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
                const newScale = computeBoxScale(w);
                if (boxScale.value !== newScale) boxScale.value = newScale;
                isMobile.value = window.innerWidth < 768;
            });
            resizeObs.observe(carouselWrapRef.value);
        }

        // Pause when off-screen, resume 1s after re-entering
        const visibilityObs = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) {
                clearTimeout(resumeTimer);
                stopAutoplay();
                isPaused.value = true;
            } else if (!userPaused.value && !reducedMotion.value) {
                resumeTimer = setTimeout(() => {
                    isPaused.value = false;
                    timerKey.value++;
                    startAutoplay();
                }, 1000);
            }
        });
        if (sectionRef.value) visibilityObs.observe(sectionRef.value);
    });

    onUnmounted(() => {
        stopAutoplay();
        clearTimeout(resumeTimer);
        if (countUpRaf) cancelAnimationFrame(countUpRaf);
        if (resizeObs) resizeObs.disconnect();
    });
</script>

<template>
    <section
        ref="sectionRef"
        aria-label="Event statistics"
        class="block-2026-stats relative overflow-hidden isolate text-white flex flex-col items-start px-[var(--box-padding-lr-m)] pt-[37px] pb-[60px] gap-[24px] md:flex-row md:items-center md:px-[40px] md:py-[40px] md:gap-[40px]">
        <!-- Left: stat carousel -->
        <div
            ref="carouselWrapRef"
            class="relative w-[calc(100%+var(--box-padding-lr-m)*2)] -mx-[var(--box-padding-lr-m)] z-[4] md:w-1/2 md:mx-0 md:max-w-[566px] md:min-w-0"
            role="group"
            aria-label="Statistics carousel"
            aria-roledescription="carousel">

            <!-- Box + controls group: centers as one unit on mobile -->
            <div class="w-fit mx-auto md:mx-0 md:w-auto">
                <!-- Box wrapper: sets layout space to the box's visual size -->
                <div class="relative" :style="boxWrapperStyle">
                    <!-- The box: fixed design size, scales via CSS transform -->
                    <div class="relative overflow-visible" :style="boxStyle">
                        <!-- Shape (behind text) -->
                        <div class="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                            <CanvasMorphShape
                                ref="morphShapeEl"
                                :paths="shapePaths"
                                :active-index="currentShapeIndex"
                                fill-color="#D63D00"
                                :drop-shadows="SHAPE_DROP_SHADOWS"
                                :inner-shadows="SHAPE_INNER_SHADOWS"
                                :width="BOX_W"
                                :height="BOX_H"
                                :bleed="bleed"
                                :transition-duration="600" />
                        </div>

                        <!-- Stat text (above shape) — fixed sizes, transform handles scaling -->
                        <div class="relative z-[1] w-full h-full flex flex-col items-center justify-center gap-[8px]"
                            aria-live="polite" aria-atomic="true">
                            <Transition name="stat-value" mode="out-in" @enter="startCountUp">
                                <p :key="currentIndex"
                                    class="font-hubspot-serif text-[calc(78/var(--base-fs)*1rem)] leading-[1] md:text-[calc(110/var(--base-fs)*1rem)]">
                                    {{ formattedValue }}
                                </p>
                            </Transition>
                            <Transition name="stat-label" mode="out-in">
                                <p :key="currentIndex"
                                    class="font-hubspot-serif text-[calc(18/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.36px] md:text-[calc(23/var(--base-fs)*1rem)] md:tracking-[0.46px]">
                                    {{ parsedStats[currentIndex].label }}
                                </p>
                            </Transition>
                        </div>
                    </div>
                </div>

                <!-- Carousel controls — pause + dots -->
                <div class="flex relative z-[2] items-center gap-[24px] mt-2 ml-[24px] md:mt-0 md:ml-0 md:absolute md:left-[8px] md:bottom-[10px]" role="group" aria-label="Carousel controls">
                <!-- Pause button -->
                <button
                    class="group relative w-[42px] h-[42px] rounded-full bg-transparent cursor-pointer p-0 border-none flex items-center justify-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    :aria-label="isPaused ? 'Play carousel' : 'Pause carousel'"
                    @click="togglePause">
                    <svg class="absolute inset-0" viewBox="0 0 42 42" fill="none" aria-hidden="true">
                        <circle v-if="isPaused" cx="21" cy="21" r="20" stroke-width="1"
                            class="stroke-white fill-transparent transition-[fill,stroke] duration-200 group-hover:fill-white group-hover:stroke-white" />
                        <circle
                            v-else
                            :key="timerKey"
                            cx="21"
                            cy="21"
                            r="20"
                            stroke-width="1"
                            transform="rotate(-90 21 21)"
                            class="stroke-white fill-transparent stats-timer-ring transition-[fill,stroke] duration-200 group-hover:fill-white group-hover:stroke-white"
                            :stroke-dasharray="CIRCUMFERENCE"
                            :stroke-dashoffset="CIRCUMFERENCE" />
                    </svg>
                    <svg v-if="!isPaused" aria-hidden="true" class="relative" width="9" height="11" viewBox="0 0 9 11" fill="none">
                        <rect width="2.5" height="11" rx="0.5" class="fill-white transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                        <rect x="6.5" width="2.5" height="11" rx="0.5" class="fill-white transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                    </svg>
                    <svg v-else aria-hidden="true" class="relative ml-[1px]" width="9" height="11" viewBox="0 0 9 11" fill="none">
                        <path d="M9 5.5L0 11V0L9 5.5Z" class="fill-white transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                    </svg>
                </button>

                <!-- Dots -->
                <div class="flex items-center gap-3.5 md:gap-2" role="tablist" aria-label="Carousel slides">
                    <button
                        v-for="(stat, i) in stats"
                        :key="i"
                        :aria-label="'Go to slide ' + (i + 1)"
                        :aria-selected="currentIndex === i ? 'true' : 'false'"
                        role="tab"
                        class="w-[12px] h-[12px] md:w-[8px] md:h-[8px] rounded-full border-none cursor-pointer p-0 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
                        :class="currentIndex === i ? 'bg-white' : 'bg-[var(--bright-amber-light)]'"
                        @click="goToSlide(i)" />
                </div>
            </div>
            </div>
        </div>

        <!-- Right: static content -->
        <div class="relative flex flex-col gap-[27px] items-start w-full md:flex-1 md:min-w-0">
            <div class="flex flex-col gap-[14px]">
                <h2 v-if="headline" class="font-hubspot-serif font-normal text-[calc(34/var(--base-fs)*1rem)] leading-[1.1] md:text-[calc(60/var(--base-fs)*1rem)]" v-html="headline" />
                <div v-if="copy" class="font-hubspot-sans font-light text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]"
                    v-html="copy" />
            </div>
            <!-- CTA Button -->
            <a v-if="button?.url"
                :href="button.url"
                :title="button.title || button.label"
                class="relative font-hubspot-serif inline-flex items-center gap-[10px] px-[20px] py-[10px] bg-white text-[var(--magenta)] no-underline border-none hover:bg-(--magenta) hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-(--orange) rounded-[50px] text-[calc(16/var(--base-fs)*1rem)] leading-[1.3] tracking-[0.32px] md:text-[calc(17/var(--base-fs)*1rem)] md:leading-[1.4] md:tracking-[0.34px] transition-colors duration-200 ease-in-out">
                {{ button.label || 'Learn More' }}
            </a>
        </div>

        <!-- Background (decorative) -->
        <div aria-hidden="true" class="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
            <img :src="statsBackgroundSvg" loading="lazy" decoding="async" alt=""
                class="absolute w-full h-full
                    top-auto bottom-0 left-[20%]
                    md:left-0 md:inset-0" />
        </div>
    </section>
</template>

<style>
    /* Gradient background: amber left → orange */
    .block-2026-stats {
        background: linear-gradient(100.78deg, #E66000 0%, #D63D00 24.4%);
    }

    @media (min-width: 768px) {
        .block-2026-stats {
            background: linear-gradient(116.9deg, #E76200 7.99%, #D63D00 27.11%);
        }
    }

    /* Timer ring: stroke grows clockwise from 12 o'clock over SLIDE_DURATION */
    .stats-timer-ring {
        animation: stats-timer-countdown 10s linear forwards;
    }

    @keyframes stats-timer-countdown {
        to {
            stroke-dashoffset: 0;
        }
    }

    /* Stat value: exits up, enters from below */
    .stat-value-enter-active,
    .stat-value-leave-active {
        transition:
            opacity 0.4s ease,
            transform 0.4s ease;
    }
    .stat-value-enter-from {
        opacity: 0;
        transform: translateY(-30px);
    }
    .stat-value-leave-to {
        opacity: 0;
        transform: translateY(-30px);
    }

    /* Label: exits down, enters from below (slides up) */
    .stat-label-enter-active,
    .stat-label-leave-active {
        transition:
            opacity 0.4s ease,
            transform 0.4s ease;
    }
    .stat-label-enter-from {
        opacity: 0;
        transform: translateY(30px);
    }
    .stat-label-leave-to {
        opacity: 0;
        transform: translateY(30px);
    }

    /* Respect reduced motion preferences */
    @media (prefers-reduced-motion: reduce) {
        .stats-timer-ring {
            animation: none !important;
            stroke-dashoffset: 0 !important;
        }
        .stat-value-enter-active,
        .stat-value-leave-active,
        .stat-label-enter-active,
        .stat-label-leave-active {
            transition: none !important;
        }
        .stat-value-enter-from,
        .stat-value-leave-to,
        .stat-label-enter-from,
        .stat-label-leave-to {
            opacity: 1;
            transform: none;
        }
    }
</style>
